<template>
  <header
    class="border-b border-white/10 bg-slate-800 px-6 py-4 md:px-10 md:py-5"
  >
    <div class="flex items-center justify-between">
      <RouterLink to="/" class="shrink-0">
        <img
          src="/assets/rtc-logo-dark-transparent.png"
          alt="RTC4Water — Real-Time Control of Water Infrastructure"
          class="h-16 w-auto md:h-32"
        />
      </RouterLink>

      <!-- Desktop nav -->
      <nav class="hidden items-center gap-1 md:flex">
        <RouterLink
          v-for="link in links"
          :key="link.to"
          :to="link.to"
          exact-active-class="bg-white/10 text-white"
          class="rounded-full px-4 py-2 text-[15px] font-semibold text-slate-300 transition hover:bg-white/5 hover:text-white"
          >{{ t(link.key) }}</RouterLink
        >
      </nav>

      <div class="flex items-center gap-3">
        <!-- Desktop language switch -->
        <div class="hidden items-center gap-1 md:flex">
          <button
            v-for="l in LOCALES"
            :key="l.code"
            @click="setLocale(l.code)"
            :class="[
              'rounded-md px-2.5 py-1.5 text-xs font-bold transition',
              state.locale === l.code
                ? 'bg-rtcblue-500 text-white'
                : 'text-slate-300 hover:bg-white/10',
            ]"
          >
            {{ l.label }}
          </button>
        </div>

        <!-- Desktop login -> RTC4Water Platform -->
        <a
          href="https://dsc.rtc4water.com/register"
          target="_blank"
          rel="noopener noreferrer"
          class="hidden flex-col items-center md:flex"
        >
          <span
            class="rounded-full bg-rtcblue-500 px-6 py-2.5 font-semibold text-white transition hover:bg-rtcblue-400"
          >
            {{ t("nav.login") }}
          </span>
          <span
            class="mt-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400"
          >
            RTC4Water {{ t("nav.platform") }}
          </span>
        </a>

        <!-- Mobile hamburger -->
        <button
          class="rounded-md p-2 text-slate-200 hover:bg-white/10 md:hidden"
          aria-label="Menu"
          @click="mobileOpen = !mobileOpen"
        >
          <svg
            v-if="!mobileOpen"
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          >
            <line x1="4" y1="7" x2="20" y2="7" />
            <line x1="4" y1="12" x2="20" y2="12" />
            <line x1="4" y1="17" x2="20" y2="17" />
          </svg>
          <svg
            v-else
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          >
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="18" y1="6" x2="6" y2="18" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Mobile menu -->
    <div v-if="mobileOpen" class="mt-4 md:hidden">
      <nav class="flex flex-col gap-1 text-base font-semibold text-slate-200">
        <RouterLink
          v-for="link in links"
          :key="link.to"
          :to="link.to"
          class="rounded-lg px-3 py-3 hover:bg-white/10"
          exact-active-class="bg-white/10 text-white"
          @click.native="mobileOpen = false"
          >{{ t(link.key) }}</RouterLink
        >
      </nav>

      <div class="mt-4 flex items-center gap-2 px-3">
        <button
          v-for="l in LOCALES"
          :key="l.code"
          @click="setLocale(l.code)"
          :class="[
            'rounded-md px-3 py-2 text-sm font-bold transition',
            state.locale === l.code
              ? 'bg-rtcblue-500 text-white'
              : 'bg-white/10 text-slate-200 hover:bg-white/20',
          ]"
        >
          {{ l.label }}
        </button>
      </div>

      <a
        href="https://dsc.rtc4water.com/register"
        target="_blank"
        rel="noopener noreferrer"
        class="mt-4 flex flex-col items-center"
      >
        <span
          class="w-full rounded-full bg-rtcblue-500 px-5 py-3 text-center font-bold text-white transition hover:bg-rtcblue-400"
        >
          {{ t("nav.login") }}
        </span>
        <span
          class="mt-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400"
        >
          RTC4Water {{ t("nav.platform") }}
        </span>
      </a>
    </div>
  </header>
</template>

<script setup>
import { ref } from "vue";
import { RouterLink } from "vue-router";
import { t, state, setLocale, LOCALES } from "@/i18n";

const mobileOpen = ref(false);

const links = [
  { to: "/", key: "nav.home" },
  { to: "/platform", key: "nav.platform" },
  { to: "/solutions", key: "nav.solutions" },
  { to: "/projects", key: "nav.projects" },
  { to: "/about", key: "nav.about" },
  { to: "/contact", key: "nav.contact" },
];
</script>
